import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ChevronLeft, Newspaper, ChevronDown } from 'lucide-react';
import SEOHead, { generateBreadcrumbSchema } from '../components/SEOHead';
import {
  PRESS_RELEASE_TITLE,
  PRESS_RELEASE_DATE,
  PRESS_RELEASE_DATE_DISPLAY,
  PRESS_TOTAL_SYNDICATIONS,
  PRESS_FEATURED,
  PRESS_OTHER_NETWORK_AFFILIATES,
  PRESS_MAJOR_OUTLETS,
  PRESS_REMAINING_SYNDICATIONS,
} from '../data/pressArticles';

const NETWORK_BADGE = {
  CBS: 'bg-[#2D7DD2]/15 text-[#7BB6FF] border-[#2D7DD2]/40',
  NBC: 'bg-[#FFB400]/15 text-[#FFB400] border-[#FFB400]/40',
  FOX: 'bg-[#E63946]/15 text-[#FF8A92] border-[#E63946]/40',
  ABC: 'bg-[#00B894]/15 text-[#5BE3C3] border-[#00B894]/40',
  CW: 'bg-[#9B59B6]/15 text-[#C792EA] border-[#9B59B6]/40',
};

function NetworkCard({ item }) {
  const badge = NETWORK_BADGE[item.network] || NETWORK_BADGE.CBS;
  return (
    <a
      href={item.uri}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`press-network-card-${item.outlet.toLowerCase()}`}
      className="group block bg-neutral-900/60 border border-white/10 hover:border-[#00F0FF]/40 rounded-2xl p-6 transition-all"
    >
      <div className="flex items-start justify-between mb-4">
        <span className={`text-[10px] font-bold uppercase tracking-[0.18em] px-2.5 py-1 rounded-full border ${badge}`}>
          {item.network}
        </span>
        <ArrowUpRight className="w-5 h-5 text-neutral-500 group-hover:text-[#00F0FF] transition-colors" />
      </div>
      <h3 className="text-2xl font-bold text-white mb-1">{item.outlet}</h3>
      <p className="text-sm text-neutral-400 mb-3">{item.market}</p>
      <p className="text-xs text-neutral-500 leading-relaxed">{item.channel}</p>
      <p className="text-xs text-[#00F0FF] mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
        Read article →
      </p>
    </a>
  );
}

function OutletRow({ name, desc, uri, testid }) {
  return (
    <a
      href={uri}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className="group flex items-center justify-between gap-4 py-3 px-4 rounded-lg hover:bg-white/[0.03] border border-transparent hover:border-white/10 transition-colors"
    >
      <div className="min-w-0">
        <p className="text-sm font-semibold text-white truncate">{name}</p>
        {desc && <p className="text-xs text-neutral-500 truncate">{desc}</p>}
      </div>
      <ArrowUpRight className="w-4 h-4 text-neutral-600 group-hover:text-[#00F0FF] flex-shrink-0 transition-colors" />
    </a>
  );
}

export default function Press() {
  const [showAll, setShowAll] = useState(false);

  // Hostname-only label for the long syndication list — keeps the page scannable.
  const hostname = (uri) => {
    try { return new URL(uri).hostname.replace(/^www\./, ''); } catch { return uri; }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="press-page">
      <SEOHead
        title="Press Coverage — theailedger.ai"
        description={`theailedger.ai covered across CBS, NBC, FOX, and ABC affiliates plus ${PRESS_TOTAL_SYNDICATIONS - 4}+ syndicated outlets — March 2026 press release.`}
        path="/press"
        keywords="theailedger press, theailedger.ai news, AI marketplace press, CBS NBC FOX ABC AI marketplace, theailedger media coverage"
        schema={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'NewsArticle',
              headline: PRESS_RELEASE_TITLE,
              datePublished: PRESS_RELEASE_DATE,
              dateModified: PRESS_RELEASE_DATE,
              author: {
                '@type': 'Organization',
                name: 'theailedger.ai',
                url: 'https://theailedger.ai',
              },
              publisher: {
                '@type': 'Organization',
                name: 'theailedger.ai',
                logo: {
                  '@type': 'ImageObject',
                  url: 'https://theailedger.ai/logo.png',
                },
              },
              description: `theailedger.ai's launch press release was picked up across CBS, NBC, FOX, and ABC broadcast affiliates plus ${PRESS_TOTAL_SYNDICATIONS}+ syndicated outlets.`,
              mainEntityOfPage: 'https://theailedger.ai/press',
            },
            generateBreadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'Press Coverage', path: '/press' },
            ]),
          ],
        }}
      />

      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="text-[#00F0FF] font-bold tracking-tight" data-testid="press-home-link">
            theailedger.ai
          </Link>
          <Link
            to="/"
            className="text-sm text-neutral-400 hover:text-white inline-flex items-center gap-1 transition-colors"
            data-testid="press-back-link"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to marketplace
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 pt-16 pb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30 text-xs font-semibold mb-6">
          <Newspaper className="w-3.5 h-3.5" />
          Press
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight mb-5">
          As seen on <span className="text-[#00F0FF]">CBS, NBC, FOX,</span> and <span className="text-[#00F0FF]">ABC</span>.
        </h1>
        <p className="text-base sm:text-lg text-white/70 leading-relaxed max-w-2xl mb-2">
          On {PRESS_RELEASE_DATE_DISPLAY}, theailedger LLC announced the launch of its dedicated AI products marketplace across 52 categories. The release was picked up across all four major US broadcast affiliates and {PRESS_TOTAL_SYNDICATIONS}+ syndicated business news outlets.
        </p>
        <p className="text-sm text-neutral-500 mb-8" data-testid="press-release-title">
          <span className="text-neutral-300 font-medium">{PRESS_RELEASE_TITLE}</span>
        </p>
      </section>

      {/* Featured network articles — CBS / NBC / FOX / ABC */}
      <section className="max-w-6xl mx-auto px-6 pb-16" data-testid="press-featured-section">
        <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#00F0FF]">Featured</span>
          <span className="text-white/40">·</span>
          <span>Network broadcast affiliates</span>
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PRESS_FEATURED.map((item) => (
            <NetworkCard key={item.outlet} item={item} />
          ))}
        </div>
      </section>

      {/* Additional network affiliates */}
      {PRESS_OTHER_NETWORK_AFFILIATES.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 pb-16" data-testid="press-network-section">
          <h2 className="text-xl font-bold mb-6">
            Additional network affiliates ({PRESS_OTHER_NETWORK_AFFILIATES.length})
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {PRESS_OTHER_NETWORK_AFFILIATES.map((item) => (
              <OutletRow
                key={item.outlet}
                name={`${item.outlet} · ${item.network}`}
                desc={`${item.market} · ${item.channel}`}
                uri={item.uri}
                testid={`press-network-row-${item.outlet.toLowerCase()}`}
              />
            ))}
          </div>
        </section>
      )}

      {/* Major outlets & business press */}
      <section className="max-w-6xl mx-auto px-6 pb-16" data-testid="press-majors-section">
        <h2 className="text-xl font-bold mb-6">
          Major outlets &amp; business press
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {PRESS_MAJOR_OUTLETS.map((item) => (
            <OutletRow
              key={item.uri}
              name={item.name}
              desc={item.desc}
              uri={item.uri}
              testid={`press-major-row-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            />
          ))}
        </div>
      </section>

      {/* Full syndication list — collapsible */}
      <section className="max-w-6xl mx-auto px-6 pb-24" data-testid="press-syndications-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">
            Full syndication list ({PRESS_REMAINING_SYNDICATIONS.length})
          </h2>
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="text-xs uppercase tracking-wider text-neutral-300 hover:text-white border border-white/10 hover:border-white/30 rounded-full px-3 py-1.5 transition inline-flex items-center gap-1.5"
            data-testid="press-syndications-toggle"
          >
            {showAll ? 'Collapse' : 'Show all'}
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showAll ? 'rotate-180' : ''}`} />
          </button>
        </div>
        {showAll && (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-sm"
            data-testid="press-syndications-list"
          >
            {PRESS_REMAINING_SYNDICATIONS.map((item, idx) => (
              <a
                key={item.uri}
                href={item.uri}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center justify-between gap-2 py-1.5 px-3 rounded hover:bg-white/[0.03] transition-colors"
                data-testid={`press-syndication-${idx}`}
              >
                <span className="text-neutral-400 truncate group-hover:text-white">{hostname(item.uri)}</span>
                <ArrowUpRight className="w-3 h-3 text-neutral-700 group-hover:text-[#00F0FF] flex-shrink-0" />
              </a>
            ))}
          </div>
        )}
        {!showAll && (
          <p className="text-sm text-neutral-500">
            Plus {PRESS_REMAINING_SYNDICATIONS.length} additional regional papers, business news platforms, and trade publications. Click <em>Show all</em> to see the full list.
          </p>
        )}
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between text-sm text-neutral-500">
          <span>© theailedger.ai</span>
          <Link to="/" className="hover:text-white transition-colors">Back to marketplace →</Link>
        </div>
      </footer>
    </div>
  );
}
